import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { catalogService } from '../services/api';
import './Catalogs.css';

const Catalogs = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  useEffect(() => {
    loadCatalogs();
  }, []);

  const loadCatalogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await catalogService.getAll();
      if (response.success) {
        setCatalogs(response.data);
      } else {
        setError('Error al cargar catálogos');
      }
    } catch (err) {
      console.error('Error loading catalogs:', err);
      setError(err.response?.data?.message || 'Error al cargar catálogos');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async (catalog) => {
    try {
      setQrLoading(true);
      setSelectedCatalog(catalog);
      const response = await catalogService.generateQR(catalog.filename);
      if (response.success) {
        setQrCode(response.data);
      } else {
        setError('Error al generar código QR');
      }
    } catch (err) {
      console.error('Error generating QR:', err);
      setError(err.response?.data?.message || 'Error al generar código QR');
    } finally {
      setQrLoading(false);
    }
  };

  const handleCloseQRModal = () => {
    setSelectedCatalog(null);
    setQrCode(null);
  };

  const handleDownloadQR = () => {
    if (!qrCode) return;

    const link = document.createElement('a');
    link.href = qrCode.qrCode;
    link.download = `QR-${selectedCatalog.name}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintQR = () => {
    if (!qrCode) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${selectedCatalog.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            h1 {
              margin-bottom: 20px;
              text-align: center;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            .info {
              margin-top: 20px;
              text-align: center;
              font-size: 14px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Catálogo: ${selectedCatalog.name}</h1>
          <img src="${qrCode.qrCode}" alt="QR Code" />
          <div class="info">
            <p>Escanea este código QR para ver el catálogo</p>
            <p>Centro Oftalmológico Los Dos Caminos</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewCatalog = (catalog) => {
    const catalogUrl = `${window.location.protocol}//${window.location.host}${catalog.url}`;
    window.open(catalogUrl, '_blank');
  };

  return (
    <div className="catalogs-page">
      <nav className="dashboard-nav">
        <div className="container">
          <div className="nav-content">
            <div className="nav-brand">
              <h2>COL2C</h2>
            </div>
            <div className="nav-user">
              <span className="user-name">
                {user?.firstName} {user?.lastName}
              </span>
              <span className="user-role">{user?.role}</span>
              <button onClick={() => navigate('/dashboard')} className="btn btn-secondary btn-sm">
                Dashboard
              </button>
              <button onClick={() => navigate('/profile')} className="btn btn-secondary btn-sm">
                Mi Perfil
              </button>
              <button onClick={handleLogout} className="btn btn-secondary btn-sm">
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="catalogs-content">
        <div className="container">
          <div className="catalogs-header">
            <h1>Catálogos de Productos</h1>
            <p>Gestiona y comparte catálogos con clientes potenciales</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Cargando catálogos...</p>
            </div>
          ) : (
            <div className="catalogs-grid">
              {catalogs.map((catalog, index) => (
                <div key={index} className="catalog-card">
                  <div className="catalog-icon">📄</div>
                  <h3>{catalog.name}</h3>
                  <p className="catalog-size">{catalog.size}</p>
                  <div className="catalog-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewCatalog(catalog)}
                    >
                      Ver Catálogo
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleGenerateQR(catalog)}
                    >
                      Generar QR
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {catalogs.length === 0 && !loading && (
            <div className="empty-state">
              <p>No hay catálogos disponibles</p>
            </div>
          )}
        </div>
      </div>

      {selectedCatalog && (
        <div className="modal-overlay" onClick={handleCloseQRModal}>
          <div className="modal-content qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Código QR - {selectedCatalog.name}</h2>
              <button className="close-btn" onClick={handleCloseQRModal}>×</button>
            </div>
            <div className="modal-body">
              {qrLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Generando código QR...</p>
                </div>
              ) : qrCode ? (
                <>
                  <div className="qr-display">
                    <img src={qrCode.qrCode} alt="QR Code" />
                  </div>
                  <div className="qr-info">
                    <p><strong>Catálogo:</strong> {selectedCatalog.name}</p>
                    <p><strong>Tamaño:</strong> {selectedCatalog.size}</p>
                    <p className="qr-instructions">
                      Escanea este código QR con tu teléfono para ver el catálogo directamente
                    </p>
                  </div>
                  <div className="qr-actions">
                    <button className="btn btn-primary" onClick={handleDownloadQR}>
                      Descargar QR
                    </button>
                    <button className="btn btn-secondary" onClick={handlePrintQR}>
                      Imprimir QR
                    </button>
                  </div>
                </>
              ) : (
                <p>Error al generar código QR</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogs;
